import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Gem, Mail, ArrowRight, ArrowLeft, Check } from "lucide-react";

const AuthShell = ({ children, title, sub }) => (
  <div className="min-h-screen flex">
    <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r border-white/5">
      <div className="absolute inset-0" style={{
        background: "radial-gradient(ellipse 60% 50% at 30% 40%, rgba(244,208,63,0.18), transparent 60%), radial-gradient(ellipse 50% 40% at 70% 70%, rgba(91,44,111,0.3), transparent 60%)"
      }} />
      <div className="noise-overlay" />
      <div className="relative flex flex-col justify-between p-12 w-full">
        <Link to="/" className="flex items-center gap-2.5">
          <Gem className="w-7 h-7 text-gold" />
          <span className="font-display text-xl font-semibold text-white">My Leads Convert</span>
        </Link>
        <div>
          <h2 className="font-display text-4xl text-white leading-tight mb-4">
            Password trouble? We'll email you a reset link — no support ticket needed.
          </h2>
          <div className="text-sm text-zinc-400">Links expire after 30 minutes for your security.</div>
        </div>
        <div className="text-xs text-zinc-600 font-mono">my leads convert · real records · grounded drafts</div>
      </div>
    </div>
    <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
      <div className="w-full max-w-md">
        <div className="lg:hidden mb-8">
          <Link to="/" className="flex items-center gap-2.5">
            <Gem className="w-7 h-7 text-gold" />
            <span className="font-display text-xl font-semibold text-white">My Leads Convert</span>
          </Link>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-white mb-2">{title}</h1>
        <p className="text-zinc-400 mb-8">{sub}</p>
        {children}
      </div>
    </div>
  </div>
);

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <AuthShell title="Check your inbox" sub={`If an account exists for ${email}, we just sent a password-reset link. It expires in 1 hour.`}>
        <div className="glass-card p-5 flex items-start gap-3 border-emerald-500/30 bg-emerald-500/[0.04]" data-testid="forgot-success">
          <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-zinc-300">
            Didn't get it? Check spam, or wait a minute and try again. The email comes from <span className="text-white">onboarding@resend.dev</span>.
          </div>
        </div>
        <Link to="/login" className="btn-ghost w-full mt-6" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Forgot password" sub="Enter your email and we'll send you a reset link.">
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <div>
          <label className="label-eyebrow block mb-2">Email</label>
          <div className="relative">
            <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-zinc-500" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-glass pl-10"
              placeholder="you@company.com"
              data-testid="forgot-email"
            />
          </div>
        </div>
        {error && <div className="text-sm text-rose-400" data-testid="forgot-error">{error}</div>}
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="forgot-submit">
          {busy ? "Sending..." : <>Send reset link <ArrowRight className="w-4 h-4" /></>}
        </button>
        <Link to="/login" className="text-sm text-zinc-400 hover:text-white flex items-center justify-center gap-1.5 pt-2" data-testid="forgot-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </Link>
      </form>
    </AuthShell>
  );
}
